import { Bell } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

/**
 * OKS-641 — Per-row "submissions to review" badge for the Brand Mission
 * list. Sits beside `MissionListCollabCountBadge` in the collabs cell.
 *
 * Renders NOTHING when `pendingReviewCount <= 0` (a clean mission adds no
 * noise — UX Spec §3 empty state). Amber/attention treatment distinguishes
 * it from the neutral outline collab-count badge; the count is capped at
 * `9+` to protect the mobile row layout (PRD Open-Q). Colour is never the
 * sole signal — the badge always carries the number plus an `aria-label`
 * (and `title` tooltip) spelling out the meaning for screen-reader and
 * colourblind users (UX Spec §6). Non-focusable/informational, matching
 * the sibling collab-count badge.
 */
interface MissionListPendingReviewBadgeProps {
  pendingReviewCount: number
  className?: string
}

const DISPLAY_CAP = 9

export function MissionListPendingReviewBadge({
  pendingReviewCount,
  className,
}: MissionListPendingReviewBadgeProps) {
  const { t } = useTranslation()
  const count = pendingReviewCount ?? 0

  // Fail-closed: zero (or a missing/negative datum) renders nothing at all
  // — no "0" badge (UX Spec §3 empty + error states).
  if (count <= 0) return null

  const label = t("missions.index.row.pending_review", { count })
  const display = count > DISPLAY_CAP ? `${DISPLAY_CAP}+` : `${count}`

  return (
    <Badge
      className={cn(
        "border-transparent bg-amber-500 font-mono text-white tabular-nums dark:bg-amber-600",
        className,
      )}
      aria-label={label}
      title={label}
      data-testid="mission-list-pending-review"
    >
      <Bell aria-hidden="true" />
      {display}
    </Badge>
  )
}
